import { FormikHelpers, useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { authApi, merchantApi } from "@api";
import { ComponentFromSocial } from "@components";
import { DAYS, MONTHS, PATH_HOME, YEARS, enumExternalMethod, enumValidateKey } from "@configs";
import {
    IAddressData,
    IAmphur,
    IDate,
    IDisplay,
    IProvince,
    ISelect,
    ISocialMedia,
    ISubDistrict,
    ISurveyQuestions,
} from "@interfaces";
import {
    getInfo,
    selectAuth,
    selectMerchant,
    setCouponId,
    setExternalLogin,
    setFreePointId,
    setFromLogin,
    setInviteId,
    setLoading,
    store,
} from "@redux";
import { formatMessage, useNotify, validNationalID } from "@utils";
import { useYup } from "@validations";
import LoadingOverlay from "react-loading-overlay-ts";
import { useHistory } from "react-router";

interface IProps {
    handleChangeTerm: (e: boolean) => void;
    showTerm: boolean;
    isConfirm: boolean;
    isFullName?: boolean;
}

const INIT_ADDRESS: IAddressData = {
    address: "",
    district: "",
    province: "",
    subDistrict: "",
    postCode: "",
};

export const ModuleSocial = (props: IProps) => {
    //props
    const { handleChangeTerm, showTerm, isConfirm, isFullName } = props;
    //page hook
    const { t } = useTranslation();
    const { YubSocialSignUp } = useYup();
    const dispatch = useDispatch();
    const history = useHistory();
    const { error, success, warning } = useNotify();
    //page redux
    const { externalLogin, userInfo } = useSelector(selectAuth);
    const merchant = useSelector(selectMerchant);
    const { termCondition } = useSelector(selectMerchant);
    const staticQuestion = merchant.signUpSettings?.staticQuestion;
    // const [groupField, setGroupField] = useState<IGroupFieldSignUp[]>(userInfo?.customFields || []);
    const [ansGroup, setAnsGroup] = useState<any[]>([]);
    const [isLoaderActive, setIsLoaderActive] = useState<boolean>(false);
    const [member, setMember] = useState<any>();
    const [toggleEmail, setToggleEmail] = useState<boolean>(false);
    //page variable
    const contact = userInfo?.contact;
    const dateCurrent = new Date(contact?.dateOfBirth || "");
    const daySelects: ISelect[] = DAYS.map((item) => {
        return {
            id: item,
            label: item,
        };
    });
    const monthSelects: ISelect[] = MONTHS.map((item) => {
        return {
            id: item,
            label: item,
        };
    });
    const yearSelects: ISelect[] = YEARS.map((item) => {
        return {
            id: item,
            label: item,
        };
    });
    const [addressData, setAddressData] = useState<IAddressData>(INIT_ADDRESS);
    const [provinceList, setProvinceList] = useState<IProvince[]>([]);
    const [amphurList, setAmphurList] = useState<IAmphur[]>([]);
    const [subDistrictList, setSubDistrictList] = useState<ISubDistrict[]>([]);
    const [display, setDisplay] = useState<IDisplay>({
        province: "",
        district: "",
        subDistrict: "",
    });
    const [formCompleted, setFormCompleted] = useState(false);

    //page state
    const [date, setDate] = useState<IDate>({
        day: (dateCurrent.getDate() || "").toString(),
        month: (dateCurrent.getMonth() + 1 || "").toString(),
        year: (dateCurrent.getFullYear() || "").toString(),
        error: "",
    });
    const [errorPhone, setError] = useState<string>();
    const [surveyQuestions, setSurveyQuestions] = useState<ISurveyQuestions[]>([]);

    const initialValues: ISocialMedia = {
        fullName: "",
        fName: "",
        lName: "",
        phoneNumber: contact?.phoneNumber || "",
        dateOfBirth: "",
        acceptTermsAndConditions: false,
        email: contact?.email || "",
    };
    const registerSchema = Yup.object().shape(YubSocialSignUp);

    const fields = merchant.signUpSettings?.fields;

    const [validateKey, setValidateKey] = useState<string>("");

    const getValidateKey = async () => {
        try {
            const res = await authApi.getValidateKey({
                useIn: "SIGN_UP",
            });

            setValidateKey(res.data.result);
        } catch (err: any) {
            console.log(formatMessage(err.response));
            // error(formatMessage(err.response));
        }
    };

    useEffect(() => {
        getValidateKey();
    }, []);

    const checkValidateKey = async () => {
        if (validateKey === enumValidateKey.EMAIL) {
            const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!regex.test(values.email ? values.email : "")) {
                warning("Invalid email format");
                return;
            }
        }
        const value =
            validateKey === enumValidateKey.EMAIL
                ? values.email
                : validateKey === enumValidateKey.NATIONAL_ID
                ? values.id_card
                : validateKey === enumValidateKey.STAFF_ID
                ? values.staffID
                : validateKey === enumValidateKey.STUDENT_ID
                ? values.studentID
                : "";
        try {
            const res = await authApi.getValidateKeyValue({
                useIn: "SIGN_UP",
                validateKey: validateKey,
                value: value,
            });
            if (res.data.result) {
                const result = res.data.result;
                // ansList[e.target.name] = e.target.value;

                setValues({
                    ...values,
                    ...(result.FULLNAME && isFullName && { fullName: result.FULLNAME }),
                    ...(result.FIRSTNAME && !isFullName && { fName: result.FIRSTNAME }),
                    ...(result.LASTNAME && !isFullName && { lName: result.LASTNAME }),
                    ...(result.CHANNEL_STORE && { channelStore: result.CHANNEL_STORE }),
                    ...(result.COMPANY_NAME && { companyName: result.COMPANY_NAME }),
                    ...(result.GENDER && { gender: result.GENDER.toLocaleUpperCase() }),
                    ...(result.NATIONAL_ID && { id_card: result.NATIONAL_ID }),
                    ...(result.STAFF_ID && { staffID: result.STAFF_ID }),
                    ...(result.STUDENT_ID && { studentID: result.STUDENT_ID }),
                    ...(result.EMAIL && { email: result.EMAIL }),
                    ...(result.STAFF_AREA && { staffArea: result.STAFF_AREA }),
                });
                setDate({
                    day: result.BIRTHDATE?.slice(8, 10),
                    month: result.BIRTHDATE?.slice(5, 7),
                    year: result.BIRTHDATE?.slice(0, 4),
                });
            }
            if (res.data.message) {
                success(formatMessage(res));
            }
        } catch (err: any) {
            error(formatMessage(err.response));
        }
    };

    useEffect(() => {
        const handlePopState = (event: PopStateEvent) => {
            if (!formCompleted) {
                event.preventDefault();
                alert("You have unsaved changes. Please complete the form before leaving.");
            }
        };

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!formCompleted) {
                const message = "You have unsaved changes. Are you sure you want to leave?";
                event.returnValue = message;
                return message;
            }
        };

        window.addEventListener("popstate", handlePopState);
        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("popstate", handlePopState);
            window.removeEventListener("beforeunload", handleBeforeUnload);
            // if (!formCompleted) {
            //     localStorage.clear();
            //     window.location.reload();
            // }
        };
    }, [formCompleted]);

    useEffect(() => {
        const data: ISurveyQuestions[] =
            fields?.map((field: any) => {
                return {
                    questionId: field._id || "",
                    question: field.displayName || "",
                    answers: [],
                    propertyName: field.propertyName,
                    isRequired: field.isRequired || false,
                };
            }) || [];
        setSurveyQuestions([...data]);
    }, []);

    useEffect(() => {
        if (isConfirm) {
            setFieldValue("acceptTermsAndConditions", true);
        }
    }, [isConfirm]);

    useEffect(() => {
        if (userInfo) {
            let groupField = userInfo?.customFields || [];
            let qData: any[] = [];
            groupField.forEach((gItem, gIndex) => {
                let gObj: any = {
                    groupName: gItem.groupName,
                    isActiveOnProfile: gItem.isActiveOnProfile,
                    isActiveOnSignUp: gItem.isActiveOnSignUp,
                    isEditByAdmin: gItem.isEditByAdmin,
                    isEditByUser: gItem.isEditByUser,
                    fields: [],
                };
                gItem?.customFields.forEach((fItem, fIndex) => {
                    let outAns: any = "";
                    if (fItem.answers !== "--") {
                        outAns = fItem.answers;
                    }
                    if (fItem?.details?.multiple) {
                        if (fItem?.answers === "--") {
                            outAns = [];
                        } else {
                            outAns = fItem.answers.split(",");
                        }
                    }
                    let fObj = {
                        questionId: fItem.details._id,
                        question: fItem.displayName,
                        answers: outAns,
                        propertyName: fItem.details.propertyName,
                        isRequired: fItem.details.isRequired,
                        options: fItem?.details?.options,
                        isFreeTextAnswer: fItem?.details?.isFreeTextAnswer,
                        multiple: fItem?.details?.multiple,
                    };
                    gObj.fields.push(fObj);
                });
                qData.push(gObj);
            });
            setAnsGroup(qData);
        }
    }, [userInfo]);

    useEffect(() => {
        getAllProvince();
    }, []);

    const getAllProvince = async () => {
        let allRes = await merchantApi.getAllProvince();
        setProvinceList(allRes.data.result);
    };
    const getAmphur = async (pId: number) => {
        let allAmphur = await merchantApi.getAmphur({
            province_id: pId,
            xpage: 1,
            xlimit: 100,
        });
        setAmphurList(allAmphur.data.result);
        let newAdd = addressData;
        newAdd.province = pId.toString();
        newAdd.district = "";
        newAdd.subDistrict = "";
        newAdd.postCode = "";
        setAddressData(newAdd);
    };
    const getSubDistrictByamphur = async (aId: number) => {
        let subRes = await merchantApi.getSubDistrict({
            amphur_id: aId,
            xpage: 1,
            xlimit: 100,
        });
        setSubDistrictList(subRes.data.result);
        let newAdd = addressData;
        // newAdd.province = subRes.data.result[0].province_id;
        newAdd.district = aId.toString();
        newAdd.subDistrict = "";
        newAdd.postCode = "";
        setAddressData(newAdd);
    };
    const onChangeProvince = async (value: any) => {
        setAddressData({
            ...addressData,
            province: value,
        });
        await getAmphur(value);
    };
    const onChangeAmphur = async (value: any) => {
        setAddressData({
            ...addressData,
            district: value,
        });
        await getSubDistrictByamphur(value);
    };
    const onChangeSubDistrict = (value: any) => {
        let outSub = Object.values(subDistrictList).find((item) => item.district_id === value);
        setAddressData({
            ...addressData,
            // subDistrict: outSub?.district_id,
            subDistrict: value,
            postCode: outSub?.zipcode || "",
        });
    };
    const setAddressText = (value: any, name: string) => {
        let newAdd = {
            ...addressData,
            [name]: value,
        };
        setAddressData({
            ...newAdd,
        });
    };

    const handleChangeDay = (value: string) => {
        setDate({
            ...date,
            day: value,
            error: "",
        });
    };
    const handleChangeMonth = (value: string) => {
        setDate({
            ...date,
            month: value,
            error: "",
        });
    };
    const handleChangeYear = (value: string) => {
        setDate({
            ...date,
            year: value,
            error: "",
        });
    };

    const handleChangeAdditionField = (value: string) => {
        //
        value;
    };

    const onChangeMemberType = (value: string) => {
        setFieldValue("memberType", value);
    };

    const isMemberApply = () => {
        return merchant?.memberTypeConfig?.isActive && merchant?.memberTypeConfig?.isMemberApply;
    };
    const isEmailPrefix = () => {
        return merchant?.memberTypeConfig?.isActive && merchant?.memberTypeConfig?.isEmailPrefix;
    };

    const handleRegister = async (
        values: ISocialMedia,
        { setSubmitting }: FormikHelpers<ISocialMedia>
    ) => {
        try {
            setSubmitting(true);
            dispatch(setLoading(true));
            setIsLoaderActive(true);
            let check = false;
            let msg = "";
            const dateForm =
                date.month && date.day && date.year
                    ? new Date(date.month + "/" + date.day + "/" + date.year)
                    : null;
            const formatedSurveyQuestion = surveyQuestions.reduce(
                (res: ISurveyQuestions[], item: ISurveyQuestions, index: number) => {
                    res.push({
                        ...item,
                        freeTextAnswer: values.freeTextAnswer ? values.freeTextAnswer[index] : "",
                    });
                    return res;
                },
                []
            );
            let outAnsList: ISurveyQuestions[] = [];
            ansGroup.forEach((aItem, aIndex) => {
                if (aItem?.isActiveOnSignUp === true) {
                    aItem?.fields?.forEach((fItem: any, fIndex: number) => {
                        let checkEmpty = true;
                        if (fItem?.isRequired) {
                            if (fItem?.isFreeTextAnswer) {
                                if (fItem?.answers === "" || fItem?.answers?.length === 0) {
                                    checkEmpty = false;
                                    check = true;
                                    msg = t("message.field_validate_empty") + fItem?.question;
                                }
                            } else {
                                if (fItem?.answers?.length === 0) {
                                    checkEmpty = false;
                                    check = true;
                                    msg = t("message.field_validate_empty") + fItem?.question;
                                }
                            }
                        } else {
                            if (fItem?.isFreeTextAnswer) {
                                if (fItem?.answers === "" || fItem?.answers?.length === 0) {
                                    checkEmpty = false;
                                }
                            } else {
                                if (fItem?.answers?.length === 0) {
                                    checkEmpty = false;
                                }
                            }
                        }
                        if (checkEmpty) {
                            let outObj = {
                                answers: fItem?.isFreeTextAnswer ? [] : fItem.answers,
                                freeTextAnswer: fItem?.isFreeTextAnswer ? fItem.answers : "",
                                isRequired: fItem?.isRequired,
                                propertyName: fItem?.propertyName,
                                question: fItem?.question,
                                questionId: fItem?.questionId,
                            };
                            outAnsList.push(outObj);
                        }
                    });
                }
            });
            // checking default validations
            if (staticQuestion?.id_card?.isActive && values.id_card) {
                if (!validNationalID(values.id_card)) {
                    error("Card ID is incorrect.");
                    return;
                }
            }
            if (staticQuestion?.id_card?.isActive && staticQuestion?.id_card?.isRequired) {
                if (!values.id_card) {
                    error(
                        t("message.field_validate_empty") + t("page.pretype." + t("page.cardId"))
                    );
                    return;
                } else if (!validNationalID(values.id_card)) {
                    error("Card ID is incorrect.");
                    return;
                }
            }
            if (staticQuestion?.dateOfBirth.isActive && staticQuestion?.dateOfBirth?.isRequired) {
                if (!dateForm) {
                    error(t("page.you_should_fill_dateofbirth_field"));
                    return;
                }
            }
            if (
                staticQuestion?.email.isActive &&
                staticQuestion?.email?.isRequired &&
                !toggleEmail
            ) {
                if (!values.email) {
                    error(t("page.you_should_fill_email_field"));
                    return;
                }
            }
            if (
                staticQuestion?.email.isActive &&
                toggleEmail &&
                staticQuestion?.email?.isRequired
            ) {
                if (!values.email) {
                    error(t("page.you_should_fill_email_field"));
                    return;
                } else if (!member && values.email) {
                    error("Please verify your email");
                    return;
                }
            }
            if (
                staticQuestion?.email.isActive &&
                toggleEmail &&
                !staticQuestion?.email?.isRequired
            ) {
                if (!member && values.email) {
                    error("Please verify your email");
                    return;
                }
            }
            if (staticQuestion?.gender.isActive && staticQuestion?.gender?.isRequired) {
                if (!values.gender) {
                    error(t("page.you_should_fill_gender_field"));
                    return;
                }
            }
            const checking = outAnsList.map(
                (item) =>
                    item.isRequired &&
                    (item?.freeTextAnswer ? item?.freeTextAnswer === "" : item.answers.length === 0)
            );
            // checking custom fields
            const isRequired = checking.find((item) => item === true);
            if (isRequired) {
                error(t("page.pls_answer_questions"));
                return;
            }
            if (!staticQuestion?.address?.isRedeem) {
                if (staticQuestion?.address?.isRequired) {
                    if (
                        addressData?.address === "" ||
                        addressData?.province === "" ||
                        addressData?.district === "" ||
                        addressData?.subDistrict === "" ||
                        addressData?.postCode === ""
                    ) {
                        error(t("page.pls_answer_address"));
                        return;
                    }
                }
            }

            let outSubDisIndex = 0;
            if (addressData?.subDistrict !== "") {
                outSubDisIndex = parseInt(addressData.subDistrict);
            }
            let proObj = provinceList.find((item) => item?.province_id === addressData?.province);
            let disObj = amphurList.find((item) => item?.amphur_id === addressData.district);
            let subObj = subDistrictList.find(
                (item) => item?.district_id === addressData?.subDistrict
            );
            // let subObj = subDistrictList[parseInt(addressData?.subDistrict)];
            let fullAdd = [
                addressData?.address,
                proObj?.province_name_tha,
                disObj?.amphur_name_tha,
                subObj?.district_name_tha,
                addressData.postCode,
            ].join(" ");

            let inviteCode = store.getState().page.inviteId;
            let sellerCode = store.getState().page.sellerId;
            let couponId = store.getState().page.couponId;
            let freepointId = store.getState().page.freepointId;
            let outFull = "";
            let outF = "";
            let outL = "";
            if (values.fullName === "" && values?.fName === "") {
                check = true;
                msg = t("message.field_validate_empty") + t("page.name");
            } else {
                if (isFullName) {
                    outFull = values.fullName;
                    let nameSplit = values.fullName.split(" ");
                    outF = nameSplit[0];
                    outL = nameSplit?.[1] || "";
                } else {
                    outF = values.fName || "";
                    outL = values.lName || "";
                    outFull = values.fName + " " + values.lName;
                }
            }
            if (check) {
                error(msg);
            } else {
                await authApi.createPassword({
                    fullName: outFull,
                    firstName: outF,
                    lastName: outL,
                    acceptTermsAndConditions: values.acceptTermsAndConditions,
                    ...(dateForm && { dateOfBirth: dateForm }),
                    ...(values.id_card && { id_card: values.id_card }),
                    ...(values.email && { email: values.email }),
                    ...(values.gender && { gender: values.gender }),
                    ...(values.staffArea && { staffArea: values.staffArea }),
                    surveyQuestions: outAnsList,
                    ...(!staticQuestion?.address?.isRedeem && {
                        address: {
                            ...addressData,
                            subDistrict:
                                addressData.subDistrict !== ""
                                    ? addressData?.subDistrict.toString()
                                    : "",
                            // subDistrict: subDistrictList[subDis].district_id || "",
                        },
                        fullAddress: fullAdd,
                    }),
                    inviteId: inviteCode || "",
                    sellerId: sellerCode || "",
                    couponId: couponId || "",
                    freepointId: freepointId || "",
                    memberType: values?.memberType,
                    ...(member &&
                        member?.memberTypeGroupNameId && {
                            memberTypeGroupNameId: member.memberTypeGroupNameId,
                        }),
                    ...(member &&
                        member?.memberTypeNameId && { memberTypeNameId: member.memberTypeNameId }),
                });
                await dispatch(getInfo());
                setFormCompleted(true);
                success(t("message.sign_up.success"));
                dispatch(
                    setExternalLogin({
                        type: externalLogin || enumExternalMethod.GOOGLE,
                        isSignUp: false,
                    })
                );
                dispatch(setCouponId(""));
                dispatch(setInviteId(""));
                dispatch(setFreePointId(""));
                history.push(PATH_HOME);
                dispatch(setFromLogin(true));
            }
        } catch (errors: any) {
            setIsLoaderActive(false);
            // const errorsResponse = errors.response.data.errors;
            if (errors.response) {
                error(formatMessage(errors.response));
            } else {
                error(t("message.sign_up.fail"));
            }
        } finally {
            setSubmitting(false);
            dispatch(setLoading(false));
            setIsLoaderActive(false);
        }
        //
    };
    const onChangeGender = (value: string) => {
        setFieldValue("gender", value);
    };
    const {
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        errors,
        touched,
        setFieldValue,
        setFieldTouched,
        setValues,
    } = useFormik({
        initialValues: initialValues,
        validationSchema: registerSchema,
        enableReinitialize: true,
        onSubmit: handleRegister,
    });

    const handleChangeAnswerByIndex = (
        answer: string | string[],
        gIndex: number,
        fIndex: number
    ) => {
        let outAns: any = "";
        if (Array.isArray(answer)) {
            outAns = answer;
        } else {
            outAns = [answer];
        }
        let gList = ansGroup;
        gList[gIndex].fields[fIndex].answers = outAns;
        setAnsGroup([...gList]);
    };
    const handleAnswerText = (value: any, gIndex: number, fIndex: number) => {
        let gList = ansGroup;
        gList[gIndex].fields[fIndex].answers = value;
        setAnsGroup([...gList]);
    };

    const showTermCondition = () => {
        handleChangeTerm(true);
    };

    return (
        <LoadingOverlay active={isLoaderActive} spinner text="Loading...">
            <ComponentFromSocial
                showTermCondition={showTermCondition}
                handleBlur={handleBlur}
                handleChange={handleChange}
                errors={errors}
                touched={touched}
                values={values}
                isSubmitting={isSubmitting}
                days={daySelects}
                months={monthSelects}
                years={yearSelects}
                onChangeDay={handleChangeDay}
                onChangeMonth={handleChangeMonth}
                onChangeYear={handleChangeYear}
                onChangeAdditionField={handleChangeAdditionField}
                handleSubmit={handleSubmit}
                date={date}
                surveyQuestions={surveyQuestions}
                handleChangeAnswerByIndex={handleChangeAnswerByIndex}
                setFieldTouched={setFieldTouched}
                errorPhone={errorPhone || ""}
                setFieldValue={setFieldValue}
                setError={setError}
                showTerm={showTerm}
                termCondition={termCondition || ""}
                onChangeGender={onChangeGender}
                handleAnswerText={handleAnswerText}
                ansGroup={ansGroup}
                addressData={addressData}
                setAddressData={setAddressData}
                provinceList={provinceList}
                amphurList={amphurList}
                subDistrictList={subDistrictList}
                onChangeProvince={onChangeProvince}
                onChangeAmphur={onChangeAmphur}
                onChangeSubDistrict={onChangeSubDistrict}
                setAddressText={setAddressText}
                onChangeMemberType={onChangeMemberType}
                isFullName={isFullName}
                isMemberApply={isMemberApply()}
                setMember={setMember}
                member={member}
                toggleEmail={toggleEmail}
                setToggleEmail={setToggleEmail}
                isEmailPrefix={isEmailPrefix()}
                checkValidateKey={checkValidateKey}
                validateKey={validateKey}
            />
        </LoadingOverlay>
    );
};
